import { PROJECT_ID } from '@/constants';
import { PerfectSearchParams } from '@/types/search';
import { FetchLogsParams } from '@/types/logEntry';
import { GetPublicationsParams, PublicationsQueryParams } from './publications/publications.types';

const getPublicationsURLSearchParams = (
  providedParams: PublicationsQueryParams,
): URLSearchParams => {
  const params = new URLSearchParams();

  const validProvidedParamsArray = Object.entries(providedParams).filter(([, value]) =>
    Boolean(value),
  );

  validProvidedParamsArray.forEach(([key, value]) => {
    params.append(key, value.toString());
  });

  return params;
};

export const apiPath = {
  getBioEntityContentsStringWithQuery: ({
    searchQuery,
    isPerfectMatch,
  }: PerfectSearchParams): string => {
    const encodedQuery = encodeURIComponent(searchQuery);
    return `projects/${PROJECT_ID}/maps/*/bio_entities:search?query=${encodedQuery}&size=1000&perfectMatch=${isPerfectMatch}`;
  },
  getDrugsStringWithQuery: (searchQuery: string): string => {
    const encodedQuery = encodeURIComponent(searchQuery);
    return `projects/${PROJECT_ID}/drugs:search?query=${encodedQuery}`;
  },
  getDrugsByTargetTarget: (target: string): string =>
    `projects/${PROJECT_ID}/drugs:search?target=${target}`,
  getChemicalsByTargetTarget: (target: string): string =>
    `projects/${PROJECT_ID}/chemicals:search?target=${target}`,
  getModelsString: (): string => `projects/${PROJECT_ID}/maps/?size=10000`,
  getModelElements: (modelId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/elements/?size=10000`,
  getModelElement: (modelId: number, elementId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/elements/${elementId}`,
  updateElementGlyph: (modelId: number, elementId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/elements/${elementId}/glyph/`,
  updateElementSubmap: (modelId: number, elementId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/elements/${elementId}/submap`,
  updateElement: (modelId: number, elementId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/elements/${elementId}`,
  addElementAnnotation: (modelId: number, elementId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/elements/${elementId}/annotations/`,
  updateElementAnnotation: (modelId: number, elementId: number, annotationId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/elements/${elementId}/annotations/${annotationId}`,
  deleteElementAnnotation: (modelId: number, elementId: number, annotationId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/elements/${elementId}/annotations/${annotationId}`,
  getShapes: (): string => `projects/${PROJECT_ID}/shapes/`,
  getLineTypes: (): string => `projects/${PROJECT_ID}/line_types/`,
  getArrowTypes: (): string => `projects/${PROJECT_ID}/arrow_types/`,
  getLayers: (modelId: number): string => `projects/${PROJECT_ID}/maps/${modelId}/layers/`,
  getLayerTexts: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/texts/`,
  getLayerRects: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/rects/`,
  addLayerRect: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/rects/`,
  getLayerRect: (modelId: number, layerId: number, rectId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/rects/${rectId}`,
  updateLayerRect: (modelId: number, layerId: number, rectId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/rects/${rectId}`,
  removeLayerRect: (modelId: number, layerId: number, rectId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/rects/${rectId}`,
  getLayerOvals: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/ovals/`,
  getLayerOval: (modelId: number, layerId: number, ovalId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/ovals/${ovalId}`,
  addLayerOval: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/ovals/`,
  updateLayerOval: (modelId: number, layerId: number, ovalId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/ovals/${ovalId}`,
  removeLayerOval: (modelId: number, layerId: number, ovalId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/ovals/${ovalId}`,
  getLayerLines: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/lines/`,
  getLayerImages: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/images/`,
  storeLayer: (modelId: number): string => `projects/${PROJECT_ID}/maps/${modelId}/layers/`,
  updateLayer: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}`,
  removeLayer: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}`,
  addLayerImageObject: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/images/`,
  updateLayerImageObject: (modelId: number, layerId: number, imageId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/images/${imageId}`,
  removeLayerImageObject: (modelId: number, layerId: number, imageId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/images/${imageId}`,
  getLayerImageObject: (modelId: number, layerId: number, imageId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/images/${imageId}`,
  addLayerText: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/texts/`,
  getLayerText: (modelId: number, layerId: number, textId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/texts/${textId}`,
  updateLayerText: (modelId: number, layerId: number, textId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/texts/${textId}`,
  removeLayerText: (modelId: number, layerId: number, textId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/texts/${textId}`,
  addLayerLine: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/lines/`,
  getLayerLine: (modelId: number, layerId: number, lineId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/lines/${lineId}`,
  updateLayerLine: (modelId: number, layerId: number, lineId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/lines/${lineId}`,
  removeLayerLine: (modelId: number, layerId: number, lineId: number | string): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}/lines/${lineId}`,
  getLayer: (modelId: number, layerId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/layers/${layerId}`,
  getGlyphImage: (glyphId: number): string =>
    `projects/${PROJECT_ID}/glyphs/${glyphId}/file_content`,
  getGlyphs: (): string => `projects/${PROJECT_ID}/glyphs/`,
  addGlyph: (): string => `projects/${PROJECT_ID}/glyphs/`,
  getReactionsForModel: (modelId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/?size=10000`,
  getReaction: (modelId: number, reactionId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}`,
  updateReaction: (modelId: number, reactionId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}`,
  updateReactionLine: (modelId: number, reactionId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}/line/`,
  updateReactantLine: (modelId: number, reactionId: number, reactantId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}/reactants/${reactantId}/line/`,
  updateProductLine: (modelId: number, reactionId: number, productId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}/products/${productId}/line/`,
  updateModifierLine: (modelId: number, reactionId: number, modifierId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}/modifiers/${modifierId}/line/`,
  updateOperatorLine: (modelId: number, reactionId: number, operatorId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}/operators/${operatorId}/line/`,
  addReactionAnnotation: (modelId: number, reactionId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}/annotations/`,
  updateReactionAnnotation: (modelId: number, reactionId: number, annotationId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}/annotations/${annotationId}`,
  deleteReactionAnnotation: (modelId: number, reactionId: number, annotationId: number): string =>
    `projects/${PROJECT_ID}/maps/${modelId}/bio_entities/reactions/${reactionId}/annotations/${annotationId}`,
  getChemicalsStringWithQuery: (searchQuery: string): string => {
    const encodedQuery = encodeURIComponent(searchQuery);
    return `projects/${PROJECT_ID}/chemicals:search?query=${encodedQuery}`;
  },
  getAllOverlaysByProjectIdQuery: (
    projectId: string,
    { publicOverlay }: { publicOverlay: boolean },
  ): string => `projects/${projectId}/overlays/?isPublic=${String(publicOverlay)}`,
  getProjectById: (projectId: string): string => `projects/${projectId}`,
  getProjectLogs: (searchQuery: FetchLogsParams): string => {
    const url = `projects/${searchQuery.projectId}/log_entries/?`;
    const params = [];
    if (searchQuery.level) {
      params.push(`level=${searchQuery.level}`);
    }
    if (searchQuery.length) {
      params.push(`size=${searchQuery.length}`);
    }
    if (searchQuery.sortColumn) {
      const sortOrder = searchQuery.sortOrder ? `,${searchQuery.sortOrder}` : '';
      params.push(`sort=${searchQuery.sortColumn}${sortOrder}`);
    }
    if (searchQuery.search) {
      params.push(`search=${encodeURIComponent(searchQuery.search)}`);
    }
    if (searchQuery.page) {
      params.push(`page=${searchQuery.page}`);
    }
    return url + params.join('&');
  },
  getProjects: (): string => `projects/`,
  postLogin: (): string => `doLogin`,
  tokenRefresh: (): string => `token/refresh`,
  tokenRevoke: (): string => `token/revoke`,
  tokenOAuth: (): string => `token/oauth`,
  postRequestResetPassword: (login: string): string => `users/${login}:request_reset_password`,
  postResetPassword: (): string => `users:reset_password`,
  postConfirmEmail: (login: string, token: string): string =>
    `users/${login}:confirm_email?token=${encodeURIComponent(token)}`,
  registerUser: (): string => `users:register_user`,
  getConfigurationOptions: (): string => 'configuration/options/',
  getMiriamTypes: (): string => 'configuration/identifier_types/',
  getMapFormats: (): string => 'configuration/map_formats/',
  getImageFormats: (): string => 'configuration/image_formats/',
  getStatus: (): string => 'status/',
  updateConfiguration: (id: string): string => `configuration/options/${id}`,
  getOverlayBioEntity: ({ overlayId, modelId }: { overlayId: number; modelId: number }): string =>
    `projects/${PROJECT_ID}/overlays/${overlayId}/maps/${modelId}/bio_entities/`,
  createOverlay: (projectId: string): string => `projects/${projectId}/overlays/`,
  getZipFromGit: (): string => `files:get_zip_from_git`,
  initiateFileUpload: (): string => `files`,
  uploadFileChunk: (fileId: number, chunkNumber: number): string =>
    `files/${fileId}/chunk/${chunkNumber}`,
  completeFileUpload: (fileId: number): string => `files/${fileId}:complete`,
  getStatisticsById: (projectId: string): string => `projects/${projectId}/statistics`,
  downloadNetworkCsv: (): string =>
    `projects/${PROJECT_ID}/maps/*/bio_entities/reactions:download_csv`,
  getAllUserOverlaysByCreatorQuery: ({
    publicOverlay,
    creatorId,
  }: {
    publicOverlay: boolean;
    creatorId: number;
  }): string =>
    `projects/${PROJECT_ID}/overlays/?size=10000&creatorId=${creatorId}&isPublic=${String(
      publicOverlay,
    )}`,
  getOverlay: (overlayId: number): string => `projects/${PROJECT_ID}/overlays/${overlayId}/`,
  updateProjectOverlay: (projectId: string, overlayId: number): string =>
    `projects/${projectId}/overlays/${overlayId}`,
  setOverlayOwner: (projectId: string, overlayId: number): string =>
    `projects/${projectId}/overlays/${overlayId}/owner`,
  removeProjectOverlay: (projectId: string, overlayId: number): string =>
    `projects/${projectId}/overlays/${overlayId}`,
  downloadElementsCsv: (): string =>
    `projects/${PROJECT_ID}/maps/*/bio_entities/elements:download_csv`,
  downloadProjectOverlay: (projectId: string, overlayId: number): string =>
    `projects/${projectId}/overlays/${overlayId}:download_source`,
  downloadOverlayLegend: (overlayId: number): string =>
    `projects/${PROJECT_ID}/overlays/${overlayId}:download_legend`,
  getSourceFile: (): string => `projects/${PROJECT_ID}:download_source`,
  downloadMap: (mapId: number): string => `/projects/${PROJECT_ID}/maps/${mapId}:download_map`,
  downloadImage: (mapId: number): string => `/projects/${PROJECT_ID}/maps/${mapId}:download_image`,
  getGeneratedZipFileForProject: (): string => `/projects/${PROJECT_ID}:download_generated_zip`,
  getGeneratedZipFileLogsForProject: (): string =>
    `/projects/${PROJECT_ID}:download_generated_zip_logs`,
  getMesh: (meshId: string): string => `mesh/${meshId}`,
  getTaxonomy: (taxonomyId: string): string => `taxonomy/${taxonomyId}`,
  getPublications: ({ params, modelId = '*' }: GetPublicationsParams): string =>
    `/projects/${PROJECT_ID}/maps/${modelId}/publications/?${getPublicationsURLSearchParams(
      params,
    )}`,
  registerPlugin: (): string => `plugins/`,
  getPlugin: (pluginId: string): string => `plugins/${pluginId}`,
  updatePlugin: (pluginId: string): string => `plugins/${pluginId}/`,
  getAllPlugins: (): string => `/plugins/?size=1000`,
  addPlugin: (): string => `/plugins/`,
  removePlugin: (pluginId: string): string => `/plugins/${pluginId}/`,
  user: (userId: number): string => `users/${userId}`,
  authTokens: (userId: number): string => `users/${userId}/auth_tokens/`,
  deleteToken: (userId: number, tokenId: number): string =>
    `users/${userId}/auth_tokens/${tokenId}/`,
  addToken: (userId: number): string => `users/${userId}/auth_tokens/`,
  updateUser: (userId: number): string => `users/${userId}/`,
  getStacktrace: (code: string): string => `stacktraces/${code}`,
  submitErrorAsZip: (): string => `minervanet:submit_error_as_zip`,
  isMachineRegistered: (): string => `minervanet:is_machine_registered`,
  registerMachine: (): string => `minervanet:register_machine`,
  unregisterMachine: (): string => `minervanet:unregister_machine`,
  registerProject: (projectId: string): string =>
    `minervanet/projects/${projectId}:register_project`,
  unregisterProject: (projectId: string): string =>
    `minervanet/projects/${projectId}:unregister_project`,
  getOauthProviders: (): string => `oauth/providers/`,

  getSearchAutocomplete: (): string =>
    `projects/${PROJECT_ID}/maps/*/bio_entities/suggested_query_list`,
  getDrugAutocomplete: (): string => `projects/${PROJECT_ID}/drugs/suggested_query_list`,
  getChemicalAutocomplete: (): string => `projects/${PROJECT_ID}/chemicals/suggested_query_list`,
  getOverlayGroups: (): string => `projects/${PROJECT_ID}/overlay_groups/`,
  addOverlayGroup: (): string => `projects/${PROJECT_ID}/overlay_groups/`,
  removeOverlayGroup: (overlayGroupId: number): string =>
    `projects/${PROJECT_ID}/overlay_groups/${overlayGroupId}`,
  updateOverlayGroup: (overlayGroupId: number): string =>
    `projects/${PROJECT_ID}/overlay_groups/${overlayGroupId}`,
  getOverlayGroup: (overlayGroupId: number): string =>
    `projects/${PROJECT_ID}/overlay_groups/${overlayGroupId}`,
  getAllUsers: (): string => `users/?size=10000`,
  createUser: (): string => `users/`,
  removeUser: (userId: number): string => `users/${userId}/`,
  grantUserPrivilege: (userId: number): string => `users/${userId}:grant_privilege`,
  revokeUserPrivilege: (userId: number): string => `users/${userId}:revoke_privilege`,
  getLicenses: (): string => 'licenses/',
  getProjectLicense: (projectId: string): string => `projects/${projectId}/license`,
  createProjectFromFile: (): string => `projects:add_from_file`,
  updateProject: (projectId: string): string => `projects/${projectId}`,
  updateProjectDisease: (projectId: string): string => `projects/${projectId}/disease`,
  removeProjectDisease: (projectId: string): string => `projects/${projectId}/disease`,
  updateProjectOrganism: (projectId: string): string => `projects/${projectId}/organism`,
  removeProjectOrganism: (projectId: string): string => `projects/${projectId}/organism`,
  moveProject: (projectId: string): string => `projects/${projectId}:move`,
  updateMap: (projectId: string, mapId: number): string => `projects/${projectId}/maps/${mapId}`,
  getProjectMaps: (projectId: string): string => `projects/${projectId}/maps/?size=10000`,

  getMapAuthors: (projectId: string, mapId: number): string =>
    `projects/${projectId}/maps/${mapId}/authors/?size=10000`,
  addMapAuthor: (projectId: string, mapId: number): string =>
    `projects/${projectId}/maps/${mapId}/authors/`,
  updateMapAuthor: (projectId: string, mapId: number, authorId: number): string =>
    `projects/${projectId}/maps/${mapId}/authors/${authorId}`,
  deleteMapAuthor: (projectId: string, mapId: number, authorId: number): string =>
    `projects/${projectId}/maps/${mapId}/authors/${authorId}`,

  getMapAnnotations: (projectId: string, mapId: number): string =>
    `projects/${projectId}/maps/${mapId}/annotations/?size=10000`,
  addMapAnnotation: (projectId: string, mapId: number): string =>
    `projects/${projectId}/maps/${mapId}/annotations/`,
  updateMapAnnotation: (projectId: string, mapId: number, annotationId: number): string =>
    `projects/${projectId}/maps/${mapId}/annotations/${annotationId}`,
  deleteMapAnnotation: (projectId: string, mapId: number, annotationId: number): string =>
    `projects/${projectId}/maps/${mapId}/annotations/${annotationId}`,

  getProjectOverlays: (projectId: string): string => `projects/${projectId}/overlays/`,

  getMapComments: (projectId: string, mapId: number): string =>
    `projects/${projectId}/maps/${mapId}/comments/?size=10000`,
  addComment: (projectId: string, mapId: number): string =>
    `projects/${projectId}/maps/${mapId}/comments/`,
  removeComment: (projectId: string, mapId: number, commentId: number): string =>
    `projects/${projectId}/maps/${mapId}/comments/${commentId}`,

  removeProject: (projectId: string): string => `projects/${projectId}`,
  archiveProject: (projectId: string): string => `projects/${projectId}:archive`,
  reviveProject: (projectId: string): string => `projects/${projectId}:revive`,
  setProjectReadonly: (projectId: string): string => `projects/${projectId}/readonly`,
  setProjectTopMap: (projectId: string): string => `projects/${projectId}/top_map`,
  getProjectOwner: (projectId: string): string => `projects/${projectId}/owner`,
  setProjectOwner: (projectId: string): string => `projects/${projectId}/owner`,
  getPermalinks: (): string => `permalinks/`,
  createPermalink: (): string => `permalinks/`,
  updatePermalink: (id: number): string => `permalinks/${id}`,
  getAnnotators: (): string => `annotators/`,
  getAnnotatorTargets: (): string => `annotator_targets/`,
  getAnnotatorPresets: (): string => `annotator_presets/`,
  createAnnotatorPreset: (): string => `annotator_presets/`,
  updateAnnotatorPreset: (presetId: number): string => `annotator_presets/${presetId}`,
  deleteAnnotatorPreset: (presetId: number): string => `annotator_presets/${presetId}`,
  annotateProject: (projectId: string): string => `projects/${projectId}:annotate`,
  getProjectJobs: (projectId: string): string => `projects/${projectId}/jobs/`,
  cancelJob: (jobId: number): string => `jobs/${jobId}`,
};
